import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { useCompanies } from "@/hooks/useCompanies";
import { AddCompanyForm } from "@/components/companies/AddCompanyForm";
import { CompaniesFilterProvider } from "@/contexts/CompaniesFilterContext";
import { CompaniesContent } from "@/components/companies/CompaniesContent";
import { CompaniesFilterWrapper } from "@/components/companies/CompaniesFilterWrapper";
import { CompaniesActions } from "@/components/companies/CompaniesActions";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { Company } from "@/components/CompanyCard";
import { usePersistedViewMode } from "@/utils/viewMode";
import { useTranslation } from "react-i18next";

const Companies = () => {
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const { companies, isLoading, addCompany, fetchCompanies } = useCompanies();
	const [companiesWithJobCount, setCompaniesWithJobCount] = useState<Company[]>([]);
	const [viewMode, setViewMode] = usePersistedViewMode("companies");
	const { t } = useTranslation();

	useEffect(() => {
		const fetchJobCounts = async () => {
			if (companies.length === 0) return;

			try {
				let updatedCompanies = [];

				const { data: jobs, error } = await supabase
					.from('jobs')
					.select('company_id')
					.in('company_id', companies.map(c => c.id));

				if (error) {
					console.error(error);
				} else {
					const jobCounts = jobs.reduce((acc, job) => {
						acc[job.company_id] = (acc[job.company_id] || 0) + 1;
						return acc;
					}, {});

					console.log(jobCounts);

					updatedCompanies = companies.map(company => ({
						...company,
						jobCount: jobCounts[company.id] || 0
					}));
				}

				setCompaniesWithJobCount(updatedCompanies);
			} catch (error) {
				console.error("Error fetching job counts:", error);
				// Fallback to original companies data
				setCompaniesWithJobCount(companies);
			}
		};

		fetchJobCounts();
	}, [companies]);

	const handleAddCompany = async (newCompanyData: {
		name: string;
		address: string;
		assignee: string;
		notes: string
	}) => {
		return await addCompany(newCompanyData);
	};

	return (
		<CompaniesFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
			<div>
				<PageHeader
					title={t("companies.header")}
					actions={
						<CompaniesActions
							isGenerating={isGenerating}
							setIsGenerating={setIsGenerating}
							fetchCompanies={fetchCompanies}
							onAddCompany={() => setIsAddModalOpen(true)}
						/>
					}
				/>

				<CompaniesFilterWrapper />

				<CompaniesContent
					companies={companiesWithJobCount.length > 0 ? companiesWithJobCount : companies}
					isLoading={isLoading}
				/>

				<AddCompanyForm
					isOpen={isAddModalOpen}
					onClose={() => setIsAddModalOpen(false)}
					onSubmit={handleAddCompany}
				/>
			</div>
		</CompaniesFilterProvider>
	);
};

export default Companies;
